import { Component, Input, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { DateService } from 'src/app/service/util/date.service';
import { getVideoObject, removeExpiredVideoObject, setVideoObject } from './utils';

@Component({
  selector: 'app-custom-video-player',
  templateUrl: './custom-video-player.component.html',
  styleUrls: ['./custom-video-player.component.scss'],
})
export class CustomVideoPlayerComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() eduID: number;
  @Input() videoSrc: string;
  @Input() canSeek: boolean;
  @Output() currentTimeChange = new EventEmitter<number>();
  @Output() durationChange = new EventEmitter<number>();
  @ViewChild('videoContainer') videoContainer: ElementRef<HTMLElement>;
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;

  TAG = 'SmartSafeTbm@TBMSafeStudy';
  isInitialized = false;
  currentProgress = 0;
  currentTime = 0;
  duration = 0;
  supposedCurrentTime = 0;
  isFullScreen = false;
  isFullscreenSupported = false;
  videoRotation = false;
  videoLeft = '0px';
  videoTop = '0px';
  videoWidth = '100%';
  videoHeight = '100%';
  today = this.date.today();

  constructor(
    private date: DateService,
  ) {}

  ngOnInit() {
    removeExpiredVideoObject();
    document.addEventListener('fullscreenchange', this.onFullscreenChange.bind(this));
    window.addEventListener('orientationchange', this.onUpdateOrientation.bind(this));
    window.addEventListener('resize', this.onUpdateOrientation.bind(this));
    this.currentTimeChange.emit(0);
    this.durationChange.emit(0);
  }
  ngAfterViewInit() {
    const video = this.videoPlayer.nativeElement;
    video.addEventListener('ratechange', this.preventRateChange.bind(this));
  }

  ngOnDestroy() {
    /* clearInterval(this.interval); */
    document.removeEventListener('fullscreenchange', this.onFullscreenChange.bind(this));
    window.removeEventListener('orientationchange', this.onUpdateOrientation.bind(this));
    window.removeEventListener('resize', this.onUpdateOrientation.bind(this));
    const video = this.videoPlayer.nativeElement;
    video.removeEventListener('ratechange', this.preventRateChange.bind(this));
  }

  preventRateChange(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (video.playbackRate !== 1) {
      video.playbackRate = 1;
    }
  }

  togglePlayPause() {
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }

  updateProgress() {
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    this.currentProgress = (video.currentTime / video.duration) * 100;
    this.currentTime = video.currentTime;
    this.duration = video.duration;
    this.currentTimeChange.emit(this.currentTime);
    this.durationChange.emit(this.duration);

    if (!video.seeking) {
      this.supposedCurrentTime = Math.max(this.supposedCurrentTime, video.currentTime);
      this.saveVideoTime();
    }

    /* // 슬라이더 진행 상태 업데이트
    const slider: HTMLElement = this.videoPlayer.nativeElement.closest('.video-container').querySelector('.slider-progress');
    const sliderRange: HTMLElement = this.videoPlayer.nativeElement.closest('.video-container').querySelector('.slider-range');
    if (slider) {
      slider.style.width = `${this.currentProgress}%`;
    }
    if (sliderRange) {
      const rangeProgress = (this.supposedCurrentTime / this.duration) * 100;
      sliderRange.style.width = `${rangeProgress}%`;
    } */
  }

  onPlayVideo() {
    if(!this.isInitialized) {
      const video: HTMLVideoElement = this.videoPlayer.nativeElement;
      video.currentTime = this.supposedCurrentTime;
      this.isInitialized = true;
    }
  }

  onSeekingVideo() {
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    if (!this.canSeek && video.currentTime > this.supposedCurrentTime) {
      video.currentTime = this.supposedCurrentTime;
    }
  }

  seekVideo(seekTime: number) {
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    if (this.canSeek || seekTime <= this.supposedCurrentTime) {
      video.currentTime = seekTime;
    } else {
      video.currentTime = this.supposedCurrentTime;
    }
  }

  onSliderClick(event: MouseEvent) {
    const slider: HTMLElement = (event.target as HTMLElement).closest('.slider');
    const rect = slider.getBoundingClientRect();
    const clickPosition = event.clientX - rect.left;
    const clickProgress = (clickPosition / rect.width) * 100;
    const seekTime = (clickProgress / 100) * this.duration;
    this.seekVideo(seekTime);
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${this.pad(minutes, 2)}:${this.pad(secs, 2)}`;
  }

  pad(num: number, size: number): string {
    let s = num + '';
    while (s.length < size) {s = '0' + s;}
    return s;
  }

  initializeVideo() {
    this.supposedCurrentTime = this.loadVideoTime();
    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    if(video.requestFullscreen) {
      video.playbackRate = 1; // 기본 배속으로 설정
      video.currentTime = this.supposedCurrentTime;
    }
  }

  saveVideoTime() {
    const video = this.videoPlayer.nativeElement;
    if (video) {
      const currentTime = video.currentTime;
      if (isNaN(currentTime)) {return;}

      setVideoObject({
        date: this.today,
        eduID: this.eduID,
        videoTime: Math.max(this.supposedCurrentTime, currentTime)
      });
    }
  }

  loadVideoTime(): number {
    const videoObj = getVideoObject({ date: this.today, eduID: this.eduID });
    if(!videoObj?.eduID) { return 0; }
    else { return videoObj.videoTime; }
  }

  /** full screen */
  toggleFullScreen() {
    const videoContainer: HTMLElement = this.videoContainer.nativeElement;
    if (!document.fullscreenElement) {
      videoContainer.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }
  onFullscreenChange() {
    this.calcVideoLayout();
  }
  onUpdateOrientation() {
    this.calcVideoLayout();
  }
  calcVideoLayout() {
    if(document.fullscreenElement) {
      this.isFullScreen = true;
      if(window.innerWidth > window.innerHeight) {
        this.videoRotation = false;
        this.videoLeft = 0 + 'px';
        this.videoTop = 0 + 'px';
        this.videoHeight = window.innerHeight + 'px';
        this.videoWidth = window.innerWidth + 'px';
      } else {
        this.videoRotation = true;
        this.videoLeft = window.innerWidth + 'px';
        this.videoTop = 0 + 'px';
        this.videoHeight = window.innerWidth + 'px';
        this.videoWidth = window.innerHeight + 'px';
      }
    } else {
      this.isFullScreen = false;
      this.videoRotation = false;
      this.videoLeft = 0 + 'px';
      this.videoTop = 0 + 'px';
      this.videoWidth = '100%';
      this.videoHeight = '100%';
    }
  }
  
}
